import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Paper,
  Tabs,
  Tab,
  TextareaAutosize,
} from '@mui/material';
import style from '../Tools/Style';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { withRouter } from 'react-router-dom';
import { actualizarEncuesta, obtenerTiposEventoEncuesta,obtenerEncuesta } from '../../actions/EncuestaAction';
import { Snackbar, Alert } from '@mui/material';

// Componente para la vista previa de una encuesta
const SurveyPreview = ({ survey, getTipoEventoNombre }) => (
  <Paper style={{ padding: '24px' }}>
    <Typography component="h2" variant="h5" style={style.cardTitle}>
      {survey.title || 'Título de la Encuesta'}
    </Typography>
    <Typography variant="body1" color="textSecondary" gutterBottom>
      {survey.description || 'Descripción de la encuesta'}
    </Typography>
    <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
      Código: {survey.code || 'SIN_CODIGO'} | Tipo de Evento: {survey.tipoEventoEncuestaId ? getTipoEventoNombre(survey.tipoEventoEncuestaId) : 'Sin tipo seleccionado'}
    </Typography>
    {survey.questions.length === 0 && (
      <Typography variant="body1" color="textSecondary" align="center" style={{ padding: '32px' }}>
        No hay preguntas para mostrar.
      </Typography>
    )}
    {survey.questions.map((question, index) => (
      <Box key={question.id} sx={{ marginBottom: 2, padding: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
        <Typography variant="subtitle1" style={style.subheader}>
          {index + 1}. {question.text || 'Pregunta sin texto'}
        </Typography>
        <Box sx={{ marginTop: 1, marginLeft: 2 }}>
          {question.options.filter(opt => opt.trim()).map((option, oIndex) => (
            <Box key={oIndex} sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
              <input
                type="radio"
                name={`question_${question.id}`}
                disabled
                style={{ marginRight: '8px' }}
              />
              <Typography variant="body2">
                {String.fromCharCode(65 + oIndex)}. {option}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    ))}
  </Paper>
);

// Componente para una sola pregunta
const QuestionForm = ({ question, qIndex, updateQuestion, addOption, updateOption, removeOption, removeQuestion }) => (
  <Paper sx={{ p: 3, mb: 3 }} elevation={1}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Typography variant="h6">Pregunta #{qIndex + 1}</Typography>
      <Button
        variant="text"
        color="error"
        onClick={() => removeQuestion(question.id)}
        startIcon={<DeleteIcon />}
      >
        Eliminar
      </Button>
    </Box>
    <TextField
      fullWidth
      variant="outlined"
      label="Escribe tu pregunta aquí..."
      value={question.text}
      onChange={(e) => updateQuestion(question.id, 'text', e.target.value)}
      sx={{ mb: 2 }}
    />
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Typography variant="body1" color="textSecondary">Opciones de Respuesta</Typography>
      <Button
        variant="outlined"
        size="small"
        onClick={() => addOption(question.id)}
        startIcon={<AddIcon />}
      >
        Agregar Opción
      </Button>
    </Box>
    {question.options.map((option, oIndex) => (
      <Box key={oIndex} sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Typography sx={{ width: '32px', textAlign: 'center' }}>
          {String.fromCharCode(65 + oIndex)}
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder={`Opción ${String.fromCharCode(65 + oIndex)}`}
          value={option}
          onChange={(e) => updateOption(question.id, oIndex, e.target.value)}
        />
        {question.options.length > 2 && (
          <Button
            variant="text"
            color="error"
            onClick={() => removeOption(question.id, oIndex)}
            size="small"
          >
            <DeleteIcon />
          </Button>
        )}
      </Box>
    ))}
  </Paper>
);

const ModificarEncuesta = ({ match, history }) => {
  const encuestaId = match.params.encuestaId;
  
  const [survey, setSurvey] = useState({
    id:'',
    code: '',
    title: '',
    description: '',
    tipoEventoEncuestaId: '',
    questions: []
  });
  const [activeTab, setActiveTab] = useState(0); // 0 para Crear/Editar, 1 para Vista Previa
  const [tiposEvento, setTiposEvento] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingEncuesta, setLoadingEncuesta] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // success, error, warning, info
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Cargar tipos de evento
        const tiposResponse = await obtenerTiposEventoEncuesta();
        if (tiposResponse.status === 200 && tiposResponse.data) {
          setTiposEvento(tiposResponse.data);
        }

        // Cargar datos de la encuesta
        await cargarEncuesta();

      } catch (error) {
        console.error('Error al cargar datos:', error);
        setError('No se pudieron cargar los datos. Intente nuevamente.');
        mostrarSnackbar('Error al cargar los datos de la encuesta', 'error');
      } finally {
        setLoading(false);
        setLoadingEncuesta(false);
      }
    };
    
    fetchData();
  }, [encuestaId]);

  const cargarEncuesta = async () => {
  try {
    const response = await obtenerEncuesta(encuestaId);
    
    if (response.status === 200 && response.data) {
      const encuestaData = response.data;
      
      setSurvey({
        id: encuestaData.encuestaId,
        code: encuestaData.numEncuesta || '',
        title: encuestaData.titulo || '',
        description: encuestaData.descripcion || '',
        tipoEventoEncuestaId: encuestaData.tipoEventoEncuestaId || '',
        questions: encuestaData.preguntas ? encuestaData.preguntas.map(pregunta => ({
          id: pregunta.preguntaId,
          text: pregunta.texto,
          options: pregunta.opciones ? pregunta.opciones
            .sort((a, b) => a.orden - b.orden)
            .map(opcion => opcion.texto) : ['', '']
        })) : []
      });
    }
  } catch (error) {
    console.error('Error al cargar encuesta:', error);
    setError('No se pudo cargar la encuesta');
    mostrarSnackbar('Error al cargar la encuesta', 'error');
  }
};

  const addQuestion = () => {
    const newQuestion = {
      id: Date.now(),
      text: '',
      options: ['', '']
    };
    setSurvey(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
  };

  const updateQuestion = (questionId, field, value) => {
    setSurvey(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId ? { ...q, [field]: value } : q
      )
    }));
  };

  const addOption = (questionId) => {
    setSurvey(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId ? { ...q, options: [...q.options, ''] } : q
      )
    }));
  };

  const updateOption = (questionId, optionIndex, value) => {
    setSurvey(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId ? {
          ...q,
          options: q.options.map((opt, idx) => idx === optionIndex ? value : opt)
        } : q
      )
    }));
  };

  const removeOption = (questionId, optionIndex) => {
    setSurvey(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId ? {
          ...q,
          options: q.options.filter((_, idx) => idx !== optionIndex)
        } : q
      )
    }));
  };

  const removeQuestion = (questionId) => {
    setSurvey(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== questionId)
    }));
  };

  const mostrarSnackbar = (mensaje, tipo = 'success') => {
    setSnackbarMessage(mensaje);
    setSnackbarSeverity(tipo);
    setSnackbarOpen(true);
  };

  const handleSave = async () => {
    if (!survey.code || !survey.title || !survey.tipoEventoEncuestaId || survey.questions.length === 0) {
      mostrarSnackbar('Por favor completa todos los campos requeridos', 'warning'); 
      return;
    }

    const invalidQuestions = survey.questions.some(q => 
      !q.text.trim() || q.options.filter(opt => opt.trim()).length < 2
    );

    if (invalidQuestions) {
      mostrarSnackbar('Todas las preguntas deben tener texto y al menos 2 opciones válidas', 'warning'); 
      return;
    }

    try {
      setGuardando(true);
      
      const encuestaData = {
        encuestaId:survey.id,
        numEncuesta: survey.code,
        titulo: survey.title,
        descripcion: survey.description,
        tipoEventoEncuestaId: parseInt(survey.tipoEventoEncuestaId),
        preguntas: survey.questions.map((q, qIndex) => ({
          texto: q.text,
          orden: qIndex + 1,
          opciones: q.options.filter(opt => opt.trim()).map((opt, oIndex) => ({
            texto: opt.trim(),
            orden: oIndex + 1
          }))
        }))
      };

      const response = await actualizarEncuesta(encuestaId, encuestaData);
      
      if (response.status === 200 || response.status === 201) {
        mostrarSnackbar('Encuesta actualizada exitosamente', 'success');
        // Opcional: redirigir después de unos segundos
        setTimeout(() => {
          history.push('/mis-encuestas');
        }, 2000);
      } else {
        mostrarSnackbar('Error al actualizar la encuesta', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      mostrarSnackbar('Error de conexión al actualizar la encuesta', 'error');
    } finally {
      setGuardando(false);
    }
  };

  const previewSurvey = () => {
    if (survey.questions.length === 0) {
      mostrarSnackbar('Agrega al menos una pregunta para ver la vista previa', 'warning');
      return;
    }
    setActiveTab(1);
  };

  const getTipoEventoNombre = (id) => {
    const tipo = tiposEvento.find(t => t.tipoEventoEncuestaId === parseInt(id));
    return tipo ? tipo.nombre : 'Sin tipo seleccionado';
  };

  const volverAMisEncuestas = () => {
    history.push('/mis-encuestas');
  };

  if (loading || loadingEncuesta) {
    return (
      <Container component="main" maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }} color="textSecondary">
              {loadingEncuesta ? 'Cargando encuesta...' : 'Cargando tipos de evento...'}
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container component="main" maxWidth="md">
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" color="error">{error}</Typography>
          <Button 
            variant="contained" 
            onClick={volverAMisEncuestas}
            sx={{ mt: 2 }}
            startIcon={<ArrowBackIcon />}
          >
            Volver
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="md">
      <div style={style.paper}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button
            onClick={volverAMisEncuestas}
            startIcon={<ArrowBackIcon />}
            sx={{ mr: 2 }}
          >
            Volver
          </Button>
          <Box>
            <Typography component="h1" variant="h5" style={style.title}>
              Modificar Encuesta
            </Typography>
            <Typography component="h6" variant="subtitle1" color="textSecondary">
              Edita tu encuesta existente
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3 }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} aria-label="survey tabs">
            <Tab label="Editar" />
            <Tab label="Vista Previa" />
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <Box sx={{ mt: 3 }}>
            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Información de la Encuesta</Typography>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Código de Encuesta *"
                    value={survey.code}
                    onChange={(e) => setSurvey(prev => ({ ...prev, code: e.target.value }))}
                    placeholder="ENC001"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Título *"
                    value={survey.title}
                    onChange={(e) => setSurvey(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Encuesta de Satisfacción"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined" sx={{ minWidth: 250, width: 'auto' }}>
                    <InputLabel>Tipo de Evento de Encuesta *</InputLabel>
                    <Select
                      value={survey.tipoEventoEncuestaId}
                      onChange={(e) => setSurvey(prev => ({ ...prev, tipoEventoEncuestaId: Number(e.target.value) }))}
                      label="Tipo de Evento de Encuesta *"
                      disabled={loading}
                    >
                      <MenuItem value="">
                        {loading ? 'Cargando tipos de evento...' : 'Selecciona un tipo de evento'}
                      </MenuItem>
                      {tiposEvento.map((tipo) => (
                        <MenuItem key={tipo.tipoEventoEncuestaId} value={tipo.tipoEventoEncuestaId}>
                          {tipo.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextareaAutosize
                    minRows={3}
                    placeholder="Descripción de la encuesta..."
                    value={survey.description}
                    onChange={(e) => setSurvey(prev => ({ ...prev, description: e.target.value }))}
                    style={style.textarea}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Typography variant="h6" gutterBottom style={style.cardTitle}>Preguntas</Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={addQuestion}
              startIcon={<AddIcon />}
              sx={{ mb: 2 }}
            >
              Agregar Pregunta
            </Button>
            {survey.questions.length === 0 ? (
              <Typography variant="body1" color="textSecondary" align="center" sx={{ p: 4 }}>
                No hay preguntas agregadas. Haz clic en "Agregar Pregunta" para comenzar.
              </Typography>
            ) : (
              survey.questions.map((question, qIndex) => (
                <QuestionForm
                  key={question.id}
                  question={question}
                  qIndex={qIndex}
                  updateQuestion={updateQuestion}
                  addOption={addOption}
                  updateOption={updateOption}
                  removeOption={removeOption}
                  removeQuestion={removeQuestion}
                />
              ))
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<VisibilityIcon />}
                onClick={previewSurvey}
              >
                Vista Previa
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={guardando ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={guardando}
              >
                {guardando ? 'Actualizando...' : 'Actualizar Encuesta'}
              </Button>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2, pt: 2, borderTop: '1px solid #ddd' }}>
            </Box>
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ mt: 3 }}>
            <SurveyPreview survey={survey} getTipoEventoNombre={getTipoEventoNombre} />
          </Box>
        )}
      </div>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default withRouter(ModificarEncuesta);